import React, { useState, useCallback, useEffect } from "react";
import { useIntl } from "react-intl";
import { operationDownloadGleba, operationSaveGlebaDescription, operationDeleteGleba } from "~/api";
import DrawableMap from "./components/DrawableMap";
// import ExportButton from "./components/ExportButton";
import AddAreaToCarModal from "./components/AddAreaToOperationModal";
import "./styles.scss";
import { formatNumber } from "~/utils/format";
import SicorProducts from "./components/SicorProducts";
import { useOperation } from "~/hooks/useOperation";
import getAreaStartForm from "./components/FormStartRender";

const ADD_AREA_TO_CAR_MODAL = "ADD_AREA_TO_CAR_MODAL";

const OperationAreas = ({ stepLabel, setError }) => {
  const { operation, operationStartFrom } = useOperation();
  const { formatMessage } = useIntl();
  const [openModal, setOpenModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [itemsList, setItemsList] = useState([]);
  const [selectedCarIndex, setSelectedCarIndex] = useState(null);
  const [selectedGlebaId, setSelectedGlebaId] = useState(null);
  const [isAddingArea, setIsAddingArea] = useState(false);
  const [glebaDetalhesJson, setGlebaDetalhesJson] = useState("");

  const formStartRender = getAreaStartForm(operationStartFrom);

  const downloadFile = (filename, data) => {
    let link = document.createElement("a");

    const blob = new Blob([data]);
    const fileDownloadUrl = URL.createObjectURL(blob);

    link.setAttribute("href", fileDownloadUrl);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchResults = useCallback(() => {
    setLoading(true);

    if (!formStartRender) return;

    formStartRender.fetch({
      formatMessage,
      operationId: operation.id,
      setResponse: response => {
        if (response.error) {
          setError(response.error);
          return;
        }

        setItemsList(response.result);
        setLoading(false);
      }
    });
  }, [operation.id, formatMessage, setError, formStartRender]);

  const handleDeleteArea = useCallback(
    id => {
      if (selectedGlebaId === id) {
        setSelectedGlebaId(null);
      }

      operationDeleteGleba(id)
        .then(() => fetchResults())
        .catch(err => {
          setError(
            err?.response?.data?.message || formatMessage({ id: "erro-api" })
          );
        });
    },
    [formatMessage, setError, fetchResults, selectedGlebaId]
  );

  const handleDownloadArea = id => {
    operationDownloadGleba(id).then(res => {
      downloadFile(`${id}.kml`, res.data);
    });
  };

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  let selectedCar = null;
  if (selectedCarIndex !== null) selectedCar = itemsList[selectedCarIndex];

  let selectedGleba = null;
  if (selectedGlebaId) {
    if (selectedCar) {
      selectedGleba = selectedCar.Gleba.find(
        gleba => gleba.id === selectedGlebaId
      );
    } else {
      selectedGleba = itemsList.find(gleba => gleba.id === selectedGlebaId);
    }
  }

  useEffect(() => {
    if (selectedGleba) {
      let detalhes_json = selectedGleba.detalhes_json?.descricao ?? "";
      setGlebaDetalhesJson(detalhes_json);
    }
  }, [selectedGleba]);

  const handleGlebaIdDescriptionChange = (e) => {
    setGlebaDetalhesJson(e);
  };

  const saveGlebaDescription = async (descricao, selectedGleba) => {
    const detalhes_json = {
      descricao: descricao,
    };

    try {
      await operationSaveGlebaDescription({
        b3PropostaId: operation.id,
        glebaId: selectedGleba?.id,
        detalhes_json,
      });

      fetchResults();
    } catch (error) {
      setError(
        error?.response?.data?.message || formatMessage({ id: "erro-api" })
      );
    }
  };

  function calcularAreaForaDePerimetro(selectedGleba) {
    if (!selectedGleba || !selectedGleba.detalhes_json || !selectedGleba.detalhes_json.areas_fora_do_perimetro) return { areaForaDoPerimetro: 0, porcentagemForaDoPerimetro: 0 };

    let areaForaDoPerimetro = 0;
    let areaTotalDasGlebas = 0;
    let porcentagemForaDoPerimetro = 0;

    selectedGleba.detalhes_json.areas_fora_do_perimetro.forEach((a) => {
      areaForaDoPerimetro += a.area_fora_do_perimetro || 0;
      areaTotalDasGlebas += parseFloat(selectedGleba.area_ha) || 0;
    });
    porcentagemForaDoPerimetro = (areaForaDoPerimetro * 100) / areaTotalDasGlebas || 0;

    return { areaForaDoPerimetro, porcentagemForaDoPerimetro }
  }

  const { areaForaDoPerimetro, porcentagemForaDoPerimetro } = calcularAreaForaDePerimetro(selectedGleba);

  return (
    <>
      <div className="areas">
        <div className="tile is-12 is-vertical">
          <h4>{stepLabel}</h4>

          <p>{formatMessage({ id: formStartRender?.options?.descricaoId })}</p>
        </div>
        <div className="tile is-ancestor">
          <div className="tile is-3 is-vertical">
            {loading && <div className="loader" />}

            {formStartRender && (
              <>
                {formStartRender.render({
                  loading,
                  itemsList,
                  isAddingArea,
                  selectedGlebaId,
                  setOpenModal,
                  selectedCar,
                  setIsAddingArea,
                  handleDeleteArea,
                  handleDownloadArea,
                  setSelectedCarIndex,
                  setSelectedGlebaId,
                  setItemsList
                })}
              </>
            )}
          </div>

          <div className="area-info tile is-vertical is-9">
            {formStartRender.options.hasCar && selectedCar ? (
              <>
                <DrawableMap
                  key={`id-${selectedCar.B3Car.B3Area[0].id}-isEditing-${isAddingArea}`}
                  setError={setError}
                  areaInfo={selectedCar?.B3Car.B3Area}
                  glebaList={selectedCar?.Gleba}
                  layers={selectedCar?.CarLayer}
                  trackId={selectedCar?.AreaUso?.trackid}
                  operationId={operation.id}
                  isEditing={isAddingArea}
                  onChange={() => fetchResults()}
                  className="area-map"
                />

                {isAddingArea && (
                  <div className="is-adding-warning">
                    <p>{formatMessage({ id: "aviso-adicionando-desenho" })}</p>

                    <button onClick={() => setIsAddingArea(false)}>
                      {formatMessage({ id: "cancelar" })}
                    </button>
                  </div>
                )}
              </>
            ) : !formStartRender.options.hasCar && selectedGleba ? (
              <DrawableMap
                key={`id-${selectedGleba.id}`}
                setError={setError}
                areaInfo={[selectedGleba]}
                glebaList={[selectedGleba]}
                layers={[]}
                trackId={null}
                operationId={operation.id}
                isEditing={false}
                className="area-map"
              />
            ) : (
              <div className="area-map map-placeholder">
                {formatMessage({ id: "cadastre-selecione-gleba-para-ver" })}
              </div>
            )}

            {selectedGleba && (
              <div className="area-details">
                <div className="area-info">
                  <p className="area-info-header">
                    {formatMessage({ id: "detalhes-gleba" })}
                  </p>
                  <p>
                    <strong>{formatMessage({ id: "municipio" })}:</strong>{" "}
                    {selectedGleba.Municipio.nome_municipio} -{" "}
                    {selectedGleba.Municipio.Estado.nome_estado}
                  </p>
                  <p>
                    <strong>{formatMessage({ id: "area" })}:</strong>{" "}
                    {formatNumber(selectedGleba.area_ha)} ha
                  </p>
                  {selectedGleba &&
                    selectedGleba.b3_car_id !== null &&
                    selectedGleba.detalhes_json !== null &&
                    !!selectedGleba.detalhes_json?.areas_fora_do_perimetro && (
                      <p>
                        <strong>{formatMessage({ id: "area-gleba-fora-do-perimetro-do-car" })}:</strong>{" "}
                        {formatNumber(areaForaDoPerimetro)} ha ({formatNumber(porcentagemForaDoPerimetro)}%)
                      </p>
                    )}

                  <p>
                    <strong>{formatMessage({ id: "descricao" })}:</strong>{""}
                  </p>
                  <textarea
                    rows="2"
                    className="textarea"
                    type="text"
                    id="area-description"
                    value={glebaDetalhesJson}
                    onChange={(e) => handleGlebaIdDescriptionChange(e.target.value)}
                  />
                  <button
                    type="button"
                    className="area-description-button"
                    onClick={() => saveGlebaDescription(glebaDetalhesJson, selectedGleba)}
                  >
                    {formatMessage({ id: "salvar-descricao" })}
                  </button>
                </div>

                <SicorProducts glebaId={selectedGlebaId} />
              </div>
            )}
          </div>
        </div>
      </div>

      {openModal === ADD_AREA_TO_CAR_MODAL && (
        <AddAreaToCarModal
          operationId={operation.id}
          options={{
            ...(formStartRender?.options ?? {}),
            save: formStartRender?.save
          }}
          trackId={selectedCar?.AreaUso?.trackid ?? null}
          setError={setError}
          onSuccess={type => {
            if (type === "draw") {
              setIsAddingArea(true);
              setOpenModal(null);
            }
            if (type === "uploaded") {
              fetchResults();
              setOpenModal(null);
            }
          }}
          onClose={() => setOpenModal(null)}
        />
      )}
    </>
  );
};

export default OperationAreas;
